import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { C } from '../../styles/tokens';


export function SearchableSelect({ selected, onChange, options, multi = false, placeholder = 'Select an option', searchable = true }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [pos, setPos] = useState({ top: 0, left: 0, width: 240 });
    const ref = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const handler = e => {
            if (ref.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const updatePos = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        }
    };

    useEffect(() => {
        if (!open) return;
        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
        };
    }, [open]);

    const handleToggle = () => {
        if (!open) updatePos();
        setOpen(o => !o);
    };

    const isSelected = code => multi ? selected.includes(code) : selected === code;

    const pick = code => {
        if (multi) {
            onChange(isSelected(code) ? selected.filter(c => c !== code) : [...selected, code]);
        } else {
            onChange(code);
            setOpen(false);
            setSearch('');
        }
    };

    const filtered = options
        .filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
            if (!multi) return 0;
            const aSel = isSelected(a.value);
            const bSel = isSelected(b.value);
            if (aSel && !bSel) return -1;
            if (!aSel && bSel) return 1;
            return 0;
        });

    let label;
    let hasValue;
    if (multi) {
        const selectedNames = options.filter(o => selected.includes(o.value)).map(o => o.label);
        label = selected.length === 0
            ? placeholder
            : selected.length <= 2
                ? selectedNames.join(', ')
                : `${selectedNames[0]} +${selected.length - 1} more`;
        hasValue = selected.length > 0;
    } else {
        const current = options.find(o => o.value === selected);
        label = current ? current.label : placeholder;
        hasValue = Boolean(current);
    }

    return (
        <div ref={ref} style={{ flex: 1, minWidth: 0 }}>
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 8,
                    border: `1px solid ${open ? C.p600 : C.n200}`,
                    background: C.w, color: hasValue ? C.n800 : C.n400,
                    fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
                    boxSizing: 'border-box', outline: 'none',
                    boxShadow: open ? `0 0 0 3px ${C.p100}` : 'none',
                    transition: 'border-color .12s, box-shadow .12s',
                }}
            >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>{label}</span>
                <ChevronDown size={14} color={C.n400} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>

            {open && createPortal(
                <div
                    ref={menuRef}
                    style={{
                        // Portaled to document.body, so this now shares a stacking context with
                        // any open modal/sheet — must clear Bootstrap's modal z-index (1055) and
                        // MobileSheet's (901) or it renders open but invisible behind them.
                        position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
                        zIndex: 2000, background: C.w,
                        border: `1px solid ${C.n200}`, borderRadius: 10,
                        boxShadow: '0 8px 24px rgba(0,0,0,.1)',
                        overflow: 'hidden',
                    }}
                >
                    {searchable && (
                        <div style={{ padding: '8px 8px 4px' }}>
                            <input
                                type="text"
                                placeholder="Search language…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                autoFocus
                                style={{
                                    width: '100%', boxSizing: 'border-box',
                                    padding: '6px 10px', border: `1px solid ${C.n200}`, borderRadius: 6,
                                    fontSize: 12.5, color: C.n900, outline: 'none', fontFamily: 'inherit',
                                }}
                            />
                        </div>
                    )}
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '16px', textAlign: 'center', color: C.n400, fontSize: 13 }}>
                                No results
                            </div>
                        ) : filtered.map(opt => {
                            const isSel = isSelected(opt.value);
                            return (
                                <div
                                    key={opt.value}
                                    onClick={() => pick(opt.value)}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', cursor: 'pointer',
                                        background: isSel ? C.p50 : 'transparent',
                                    }}
                                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = C.n50; }}
                                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                                >
                                    <span style={{ fontSize: 13, color: isSel ? C.p600 : C.n700, fontWeight: isSel ? 600 : 400 }}>
                                        {opt.label}
                                    </span>
                                    {isSel && <Check size={13} color={C.p600} />}
                                </div>
                            );
                        })}
                    </div>
                    {multi && selected.length > 0 && (
                        <div style={{ padding: '6px 8px', borderTop: `1px solid ${C.n100}`, display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => onChange([])}
                                style={{
                                    padding: '3px 10px', borderRadius: 5, border: `1px solid ${C.n200}`,
                                    background: C.w, color: C.n500, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
}

export default SearchableSelect;
